import {
  ClipboardCopyIcon,
  CogIcon,
  MusicNoteIcon,
  PhotographIcon,
  QrcodeIcon,
  TicketIcon,
  UserGroupIcon,
  UsersIcon,
  ViewGridAddIcon,
} from "@heroicons/react/outline";
import {
  ReceiptTaxIcon,
  TemplateIcon,
  UserGroupIcon as SolidUserGroupIcon,
  ViewBoardsIcon,
} from "@heroicons/react/solid";
import { BookMarkedIcon } from "lucide-react";
import { EVoucherIcon, Runner, ShoeIcon, Ticket2Icon } from "components/customIcons";
import type { ReactNode } from "react";
import { getPermissionsUniqueParrents } from "utills/helpers";

export interface WpsMenuItem {
  id: string;
  label?: string;
  labelKey?: string;
  url: string;
  icon: ReactNode;
  subMenu?: WpsMenuItem[];
}

export interface WpsDashboardPermission {
  id: string;
  parrent: string;
  permissions: string[];
}

export const publicWpsDashboardRoutes = ["fipix", "check-in"];
export const publicWpsDashboardSubMenuRoutes = ["check-in-introduction"];

export const wpsMenuData: WpsMenuItem[] = [
  {
    id: "dashboard",
    url: "/dashboard/overview",
    labelKey: "dashboard",
    icon: <ViewBoardsIcon className="nav-icon" aria-hidden="true" />,
  },
  {
    id: "events",
    url: "",
    labelKey: "menu-event",
    icon: <TicketIcon className="nav-icon" aria-hidden="true" />,
    subMenu: [
      {
        id: "sports",
        url: "/events/sports",
        labelKey: "menu-event-sport",
        icon: <Runner className="nav-icon" aria-hidden="true" />,
      },
    ],
  },
  {
    id: "orders",
    url: "/orders",
    labelKey: "menu-orders",
    icon: <TemplateIcon className="nav-icon" aria-hidden="true" />,
  },
  {
    id: "attendees",
    url: "",
    labelKey: "attendees",
    icon: <ViewGridAddIcon className="nav-icon" aria-hidden="true" />,
    subMenu: [
      {
        id: "sports",
        url: "/attendees/sports",
        labelKey: "sport-attendees",
        icon: <Runner className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "attractions",
        url: "/attendees/attractions",
        labelKey: "attraction-attendees",
        icon: <TicketIcon className="nav-icon" aria-hidden="true" />,
      },
    ],
  },
  {
    id: "organization",
    url: "",
    labelKey: "organization",
    icon: <SolidUserGroupIcon className="nav-icon" aria-hidden="true" />,
    subMenu: [
      {
        id: "info",
        url: "/organization/info",
        labelKey: "organization-information",
        icon: <UserGroupIcon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "role",
        url: "/organization/role",
        labelKey: "role-management",
        icon: <ClipboardCopyIcon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "account",
        url: "/organization/account",
        labelKey: "account-management",
        icon: <UsersIcon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "einvoice",
        url: "/organization/einvoice",
        labelKey: "einvoice",
        icon: <ReceiptTaxIcon className="nav-icon" aria-hidden="true" />,
      },
    ],
  },
  {
    id: "promotion",
    url: "",
    labelKey: "menu-promotion",
    icon: <ReceiptTaxIcon className="nav-icon" aria-hidden="true" />,
    subMenu: [
      {
        id: "coupon",
        url: "/promotion/coupon",
        labelKey: "promotion-coupon-management",
        icon: <EVoucherIcon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "condition",
        url: "/promotion/condition",
        labelKey: "promotion-condition-management",
        icon: <CogIcon className="nav-icon" aria-hidden="true" />,
      },
    ],
  },
  {
    id: "fipix",
    url: "/fipix",
    label: "Fipix",
    icon: <PhotographIcon className="nav-icon" aria-hidden="true" />,
  },
  {
    id: "check-in",
    url: "",
    label: "Check In",
    icon: <QrcodeIcon className="nav-icon" aria-hidden="true" />,
    subMenu: [
      {
        id: "check-in-introduction",
        url: "/check-in/introduction",
        labelKey: "checkin.introduction",
        icon: <BookMarkedIcon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "events",
        url: "/check-in/events",
        labelKey: "checkin.events",
        icon: <Ticket2Icon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "sport-participants",
        url: "/check-in/sport-participants",
        labelKey: "checkin.sport-participants",
        icon: <ShoeIcon className="nav-icon" aria-hidden="true" />,
      },
      {
        id: "attraction-participants",
        url: "/check-in/attraction-participants",
        labelKey: "checkin.attraction-participants",
        icon: <MusicNoteIcon className="nav-icon" aria-hidden="true" />,
      },
    ],
  },
];

export const canViewWpsMenuItem = (
  menuItem: WpsMenuItem,
  permissions: WpsDashboardPermission[] = []
) => {
  if (publicWpsDashboardRoutes.includes(menuItem.id)) {
    return true;
  }

  const parentPermissions = getPermissionsUniqueParrents(permissions);
  if (!parentPermissions.includes(menuItem.id)) {
    return false;
  }

  const allChildrenPermissions = permissions
    .filter((permission) => permission.parrent === menuItem.id)
    .filter((permission) => permission.permissions.includes("view"));

  return allChildrenPermissions.length > 0;
};

export const canViewWpsSubMenuItem = (
  parentId: string,
  subMenuItem: WpsMenuItem,
  permissions: WpsDashboardPermission[] = []
) => {
  if (publicWpsDashboardSubMenuRoutes.includes(subMenuItem.id)) {
    return true;
  }

  const targetPermission = permissions.find(
    (permission) =>
      permission.id === subMenuItem.id && permission.parrent === parentId
  );

  return !!targetPermission?.permissions.includes("view");
};
